import {
    DEBUG_PROCESSING_RECEIPT_APP,
    DEBUG_PROCESSING_RECEIPT_HELPERS,
    DEBUG_PROCESSING_RECEIPT_ORDER,
    USE_DEBUG_PROCESSING_RECEIPT,
} from "./processingReceiptDebug.js"

function trimSubString(input, substring) {
    let result = String(input ?? "")
    if (!result || !substring) {
        return result
    }
    while (result.startsWith(substring)) {
        result = result.slice(substring.length)
    }
    while (result.endsWith(substring)) {
        result = result.slice(0, substring.length * -1)
    }
    return result
}

function formatAddressLine(person) {
    if (!person) {
        return ""
    }
    return trimSubString(
        [
            person.street_address ?? "",
            person.city ?? "",
            person.state ?? "",
            person.country ?? "",
            person.zip ?? "",
        ]
            .map((part) => String(part).trim())
            .filter(Boolean)
            .join(", "),
        ", "
    )
}
function optionsForProcess(processId, options) {
    return (options ?? []).filter((o) =>
        [o.process_id1, o.process_id2, o.process_id3, o.process_id4, o.process_id5].includes(processId)
    )
}

function calculateWeighInProcess(processId, speciesId, orderOptions, options, weighInProcesses) {
    const possibleWeighInProcesses = (weighInProcesses ?? []).filter(
        (wip) => wip.process_id === processId && wip.tuna_only === (speciesId === 10)
    )
    if (possibleWeighInProcesses.length === 1) {
        return possibleWeighInProcesses[0]
    }
    const relevantOptionIds = (orderOptions ?? [])
        .filter(
            (oo) =>
                oo.value &&
                optionsForProcess(processId, options).find((ofp) => ofp.id === oo.option_id)
        )
        .map((oo) => oo.option_id)
    const matchingWeighInProcesses = possibleWeighInProcesses.filter(
        (wip) =>
            [...new Set([wip.pin_out_option_id, wip.skin_off_option_id].filter(Boolean))]
                .sort()
                .join(",") === relevantOptionIds.sort().join(",")
    )
    if (!matchingWeighInProcesses?.length) {
        return null
    }
    return matchingWeighInProcesses.sort((a, b) => a.id - b.id)[0]
}

function sortWeighInDetailsFunc(weighInProcesses) {
    return (a, b) => {
        const aProcess = (weighInProcesses ?? []).find((x) => x.id === a.weigh_in_process_id)
        const bProcess = (weighInProcesses ?? []).find((x) => x.id === b.weigh_in_process_id)
        if (!aProcess) return 1
        if (!bProcess) return -1
        if (aProcess.process_id !== bProcess.process_id) {
            return aProcess.process_id - bProcess.process_id
        }
        if (a.species_id !== b.species_id) {
            return a.species_id - b.species_id
        }
        return a.weigh_in_process_id - b.weigh_in_process_id
    }
}

function speciesLabel(speciesId, otherSpeciesName, speciesList) {
    const raw =
        (speciesList ?? []).find((x) => x.id === speciesId)?.name ??
        otherSpeciesName ??
        ""
    return trimSubString(trimSubString(String(raw), "Salmon"), "Cod").trim()
}

function processLabel(detail, order, app) {
    const weighInProcess = calculateWeighInProcess(
        detail.process_id,
        detail.species_id,
        order.options,
        app?.options,
        app?.weighInProcesses
    )
    if (weighInProcess?.oms_name) {
        return weighInProcess.oms_name
    }
    return (
        (app?.processes ?? []).find((x) => x.id === detail.process_id)?.name ??
        "(Unknown Process)"
    )
}

function buildReceiptAddress(person, helpers) {
    const lastNameCommaFirstName =
        helpers?.lastNameCommaFirstName ??
        ((addr) => {
            const last = String(addr?.last_name ?? "").trim()
            const first = String(addr?.first_name ?? "").trim()
            return [last, first].filter(Boolean).join(", ")
        })
    const formatPhone = helpers?.formatPhone ?? ((value) => String(value ?? ""))

    return {
        name: lastNameCommaFirstName(person).toUpperCase(),
        addressLine1: formatAddressLine(person).toUpperCase(),
        addressLine2: "",
        phone: formatPhone(person?.phone ?? ""),
        email: String(person?.email ?? "").toUpperCase(),
    }
}

function shipToRegion(order, helpers, locations) {
    if (order?.pick_up_location_id) {
        return "CANADA"
    }
    const shipTo = helpers?.shipAddress?.(order) ?? order?.ship_to ?? {}
    const country = String(shipTo?.country ?? "").trim().toLowerCase()
    if (country === "canada") return "CANADA"
    if (
        country === "usa" ||
        country === "us" ||
        country === "united states" ||
        country === "united states of america"
    ) {
        return "USA"
    }
    if (country) {
        return String(shipTo.country).toUpperCase()
    }
    return "CANADA"
}

function buildAlternateShipTo(order, helpers, locations) {
    if (order?.pick_up_location_id) {
        const pickUpLocation = (locations ?? []).find(
            (location) => location.id === order.pick_up_location_id
        )
        return {
            name: "PICK UP IN STORE",
            addressLine1: String(
                pickUpLocation?.pick_up_name || pickUpLocation?.name || ""
            ).toUpperCase(),
            addressLine2: "",
            phone: "",
            email: "",
        }
    }

    if (order?.bill_same_as_ship || !order?.ship_to) {
        return undefined
    }

    return buildReceiptAddress(order.ship_to, helpers)
}

/**
 * @typedef {{ species: string, process: string, quantity: number }} ProcessingReceiptLine
 * @typedef {{ name: string, addressLine1: string, addressLine2: string, phone: string, email: string }} ProcessingReceiptAddress
 * @typedef {{
 *   masterNumber: string,
 *   shipToRegion: string,
 *   customer: ProcessingReceiptAddress,
 *   alternateShipTo?: ProcessingReceiptAddress,
 *   lines: ProcessingReceiptLine[],
 *   boxBagCount: number,
 *   depotWeightLbs: number,
 * }} ProcessingReceipt
 */

/**
 * Build mini processing receipt content from a real API order.
 *
 * @param {any} order
 * @param {number} totalBoxes
 * @param {any} app
 * @param {any} helpers
 * @returns {ProcessingReceipt}
 */
export function buildProcessingReceiptFromOrder(order, totalBoxes, app, helpers) {
    if (USE_DEBUG_PROCESSING_RECEIPT) {
        order = DEBUG_PROCESSING_RECEIPT_ORDER
        totalBoxes = DEBUG_PROCESSING_RECEIPT_ORDER.total_boxes
        app = DEBUG_PROCESSING_RECEIPT_APP
        helpers = DEBUG_PROCESSING_RECEIPT_HELPERS
    }

    const billAddressFn = helpers?.billAddress ?? helpers?.address
    const billTo = billAddressFn?.(order) ?? order?.ship_to ?? order?.bill_to ?? order ?? {}
    const customer = buildReceiptAddress(billTo, helpers)
    const alternateShipTo = buildAlternateShipTo(order, helpers, app?.locations ?? [])
    const locations = app?.locations ?? []

    let lines = []
    if (!order?.hold_for_pick_up && Array.isArray(order?.details) && order.details.length > 0) {
        lines = order.details
            .map((detail) => ({
                species: speciesLabel(
                    detail.species_id,
                    detail.other_species_name,
                    app?.species
                ),
                process: processLabel(detail, order, app),
                quantity: Math.max(1, Number(detail.num_fish) || 1),
                species_id: detail.species_id,
                process_id: detail.process_id,
                weigh_in_process_id: calculateWeighInProcess(
                    detail.process_id,
                    detail.species_id,
                    order.options,
                    app?.options,
                    app?.weighInProcesses
                )?.id,
            }))
            .filter((line) => line.species || line.process)
            .sort(sortWeighInDetailsFunc(app?.weighInProcesses))
            .map(({ species, process, quantity }) => ({ species, process, quantity }))
    }

    const weightLb = Number(order?.total_weight_lb)
    const depotWeightLbs = Number.isFinite(weightLb) && weightLb > 0
        ? Math.round(weightLb)
        : 0

    return {
        masterNumber: String(order?.master ?? ""),
        shipToRegion: shipToRegion(order, helpers, locations),
        customer,
        alternateShipTo,
        lines,
        boxBagCount: Math.max(1, Math.round(Number(totalBoxes) || 1)),
        depotWeightLbs,
    }
}
